import React from 'react';
import styles from './editor-background.css';

const LEGACY_STORAGE_KEY = 'berry:editor-background';
const SETTINGS_STORAGE_KEY = 'berry:editor-background-settings';
const DB_NAME = 'berryEditor';
const DB_STORE = 'wallpapers';
const DB_KEY = 'editorBackground';
const MAX_SIZE = 10 * 1024 * 1024;

const DEFAULT_SETTINGS = {
    size: 'cover',
    blur: 0,
    darken: 0
};

const SIZES = {
    cover: 'cover',
    contain: 'contain',
    tile: 'auto'
};

let dbPromise;

const openDB = () => {
    if (!dbPromise) {
        dbPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, 1);
            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(DB_STORE)) {
                    db.createObjectStore(DB_STORE);
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }
    return dbPromise;
};

const getWallpaper = async () => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(DB_STORE, 'readonly');
        const req = tx.objectStore(DB_STORE).get(DB_KEY);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => reject(req.error);
    });
};

const setWallpaper = async blob => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(DB_STORE, 'readwrite');
        tx.objectStore(DB_STORE).put(blob, DB_KEY);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
};

const removeWallpaper = async () => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(DB_STORE, 'readwrite');
        tx.objectStore(DB_STORE).delete(DB_KEY);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
};

const migrateFromLocalStorage = async () => {
    const dataURL = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!dataURL) return;
    try {
        const blob = await (await fetch(dataURL)).blob();
        await setWallpaper(blob);
    } catch (e) {
        // ignore
    }
    localStorage.removeItem(LEGACY_STORAGE_KEY);
};

const loadSettings = () => {
    try {
        const stored = JSON.parse(localStorage.getItem(SETTINGS_STORAGE_KEY) || '{}');
        return {
            ...DEFAULT_SETTINGS,
            ...stored
        };
    } catch (e) {
        return {...DEFAULT_SETTINGS};
    }
};

const saveSettings = settings => {
    try {
        localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
        // ignore
    }
};

class EditorBackground extends React.Component {
    constructor (props) {
        super(props);
        this.settings = loadSettings();
        this.panel = null;
        this.currentObjectUrl = null;
        this.mutationObserver = null;
        this.seenInjectionDivs = new WeakSet();
    }
    componentDidMount () {
        migrateFromLocalStorage();
        this.observe();
        this.applyAll();
    }
    componentWillUnmount () {
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
        this.revokeObjectUrl();
        this.panel = null;
    }
    observe () {
        this.mutationObserver = new MutationObserver(() => this.applyAll());
        this.mutationObserver.observe(document.documentElement, {
            childList: true,
            subtree: true
        });
    }
    applyAll () {
        const injectionDivs = document.querySelectorAll('.injectionDiv');
        for (const injectionDiv of injectionDivs) {
            if (this.seenInjectionDivs.has(injectionDiv)) continue;
            this.seenInjectionDivs.add(injectionDiv);
            this.apply(injectionDiv);
        }
    }
    revokeObjectUrl () {
        if (this.currentObjectUrl) {
            URL.revokeObjectURL(this.currentObjectUrl);
            this.currentObjectUrl = null;
        }
    }
    async apply (injectionDiv) {
        let blob = null;
        try {
            blob = await getWallpaper();
        } catch (e) {
            // ignore
        }
        let layer = injectionDiv.querySelector(`.${styles.layer}`);
        if (blob) {
            if (!layer) {
                layer = document.createElement('div');
                layer.className = styles.layer;
                injectionDiv.insertBefore(layer, injectionDiv.firstChild);
            }
            const objectUrl = URL.createObjectURL(blob);
            this.revokeObjectUrl();
            this.currentObjectUrl = objectUrl;
            const darken = this.settings.darken / 100;
            const overlay = `linear-gradient(rgba(0, 0, 0, ${darken}), rgba(0, 0, 0, ${darken}))`;
            layer.style.display = 'block';
            layer.style.backgroundImage = `${overlay}, url("${objectUrl}")`;
            layer.style.backgroundSize = `${SIZES[this.settings.size]}, cover`;
            layer.style.backgroundPosition = 'center center, center center';
            layer.style.backgroundRepeat = 'no-repeat, no-repeat';
            layer.style.filter = this.settings.blur > 0 ? `blur(${this.settings.blur}px)` : 'none';
        } else {
            this.revokeObjectUrl();
            if (layer) layer.style.display = 'none';
        }
        let button = injectionDiv.querySelector(`.${styles.button}`);
        if (!button) {
            button = document.createElement('div');
            button.className = styles.button;
            button.title = '编辑器背景';
            button.innerHTML = [
                '<svg viewBox="0 0 24 24">',
                '<path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 16H5V5h14v14z',
                'M7.5 13.5l3 3.5 2-2.5 3.5 4.5h-11l2.5-5.5z"/>',
                '</svg>'
            ].join('');
            button.addEventListener('click', e => {
                e.stopPropagation();
                if (!this.panel) this.createPanel(injectionDiv);
                this.panel.style.display = this.panel.style.display === 'block' ? 'none' : 'block';
            });
            injectionDiv.appendChild(button);
        }
    }
    createPanel (injectionDiv) {
        const panel = document.createElement('div');
        panel.className = styles.panel;
        panel.style.display = 'none';

        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.style.display = 'none';
        fileInput.addEventListener('change', () => {
            const file = fileInput.files && fileInput.files[0];
            fileInput.value = '';
            if (file) this.handleUpload(file);
        });

        const uploadBtn = document.createElement('button');
        uploadBtn.className = styles.btn;
        uploadBtn.textContent = '上传壁纸';
        uploadBtn.addEventListener('click', () => fileInput.click());

        const clearBtn = document.createElement('button');
        clearBtn.className = `${styles.btn} is-danger`;
        clearBtn.textContent = '清除壁纸';
        clearBtn.addEventListener('click', async () => {
            try {
                await removeWallpaper();
            } catch (e) {
                // ignore
            }
            panel.style.display = 'none';
            await this.apply(injectionDiv);
        });

        panel.appendChild(uploadBtn);
        panel.appendChild(clearBtn);
        panel.appendChild(fileInput);

        const controls = this.createControls(settings => {
            this.settings = settings;
            saveSettings(settings);
            this.apply(injectionDiv);
        });

        for (const control of controls) {
            panel.appendChild(control);
        }

        this.panel = panel;
        injectionDiv.appendChild(panel);
    }
    createControls (onChange) {
        const makeRow = (labelText, control) => {
            const row = document.createElement('div');
            row.className = styles.control;
            const label = document.createElement('span');
            label.textContent = labelText;
            row.appendChild(label);
            row.appendChild(control);
            return row;
        };

        const sizeSelect = document.createElement('select');
        for (const [value, name] of [
            ['cover', '铺满裁切'],
            ['contain', '完整显示'],
            ['tile', '平铺重复']
        ]) {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = name;
            sizeSelect.appendChild(option);
        }
        sizeSelect.value = this.settings.size;
        sizeSelect.addEventListener('change', () => {
            onChange({...this.settings, size: sizeSelect.value});
        });

        const blurInput = document.createElement('input');
        blurInput.type = 'range';
        blurInput.min = 0;
        blurInput.max = 30;
        blurInput.value = this.settings.blur;
        blurInput.addEventListener('input', () => {
            onChange({...this.settings, blur: Number(blurInput.value)});
        });

        const darkenInput = document.createElement('input');
        darkenInput.type = 'range';
        darkenInput.min = 0;
        darkenInput.max = 100;
        darkenInput.value = this.settings.darken;
        darkenInput.addEventListener('input', () => {
            onChange({...this.settings, darken: Number(darkenInput.value)});
        });

        return [
            makeRow('铺满方式', sizeSelect),
            makeRow('模糊程度', blurInput),
            makeRow('压暗程度', darkenInput)
        ];
    }
    async handleUpload (file) {
        if (!file.type || !file.type.startsWith('image/')) {
            // eslint-disable-next-line no-alert
            window.alert('请选择图片文件');
            return;
        }
        if (file.size > MAX_SIZE) {
            // eslint-disable-next-line no-alert
            window.alert('图片过大（超过 10MB），请选择更小的图片');
            return;
        }
        try {
            await setWallpaper(file);
            if (this.panel) this.panel.style.display = 'none';
            this.applyAll();
        } catch (e) {
            // eslint-disable-next-line no-alert
            window.alert('保存壁纸失败，请重试');
        }
    }
    render () {
        return null;
    }
}

export default EditorBackground;
